import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Search, ChevronLeft, ChevronRight, Camera, X } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import ProductCard from "../components/ProductCard";

const LIMIT = 12;

export default function Products() {
  const { t } = useLang();
  const SORTS = [
    { v: "", l: t("sort_best") },
    { v: "newest", l: t("sort_new") },
    { v: "price_asc", l: t("sort_asc") },
    { v: "price_desc", l: t("sort_desc") },
  ];
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const country = params.get("country") || "";
  const sort = params.get("sort") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10));

  const [cats, setCats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState(q);
  const [imageResults, setImageResults] = useState(location.state?.imageResults || null);
  const imageTerms = location.state?.imageTerms || [];

  useEffect(() => {
    api("/categories").then((r) => setCats(r.filter((c) => c.enabled))).catch(() => {});
  }, []);

  useEffect(() => {
    setInput(q);
    if (imageResults) {
      setProducts(imageResults);
      setLoading(false);
      return;
    }
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (country) qs.set("country", country);
    if (sort) qs.set("sort", sort);
    qs.set("page", String(page));
    qs.set("limit", String(LIMIT));
    api(`/products?${qs.toString()}`)
      .then(setProducts)
      .catch(() => setProducts([]))
      .finally(() => setLoading(false));
  }, [q, category, country, sort, page, imageResults]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const clearImageSearch = () => {
    window.history.replaceState({}, "");
    setImageResults(null);
  };

  return (
    <div data-testid="products-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
        {category ? cats.find((c) => c.key === category)?.label || t("nav_products") : country ? `${t("products_from")} ${country}` : t("all_products")}
      </h1>
      <p className="mt-1 text-sm text-slate-600">
        {imageResults ? t("photo_search") : q ? `${t("results_for")} “${q}”` : t("source_sub")}
      </p>

      {imageResults && (
        <div data-testid="image-search-banner" className="mt-4 flex flex-wrap items-center gap-2 rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3">
          <Camera className="h-4 w-4 text-teal-700" />
          <p className="text-sm text-teal-900">
            {t("photo_search")}{imageTerms.length ? `: ${imageTerms.slice(0, 3).join(", ")}` : ""}
          </p>
          <button
            data-testid="image-search-clear"
            onClick={clearImageSearch}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-teal-300 px-3 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100 transition"
          >
            <X className="h-3 w-3" /> {t("clear")}
          </button>
        </div>
      )}

      <form
        className="mt-6"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          clearImageSearch();
          update({ q: input.trim() });
        }}
      >
        <div className="relative max-w-xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            data-testid="products-search-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("search_ph2")}
            className="w-full rounded-full border border-slate-200 bg-white pl-11 pr-28 py-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
          />
          <button
            data-testid="products-search-button"
            type="submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-teal-700 px-4 py-1.5 text-xs font-bold text-white hover:bg-teal-800 transition"
          >
            {t("search") || "Search"}
          </button>
        </div>
      </form>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button
          data-testid="category-pill-all"
          onClick={() => update({ category: "" })}
          className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
            !category ? "bg-teal-700 text-white shadow" : "bg-white border border-slate-200 text-slate-700 hover:border-teal-400"
          }`}
        >
          All
        </button>
        {cats.map((c) => (
          <button
            key={c.key}
            data-testid={`category-pill-${c.key}`}
            onClick={() => update({ category: c.key === category ? "" : c.key })}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition ${
              category === c.key
                ? "bg-teal-700 text-white shadow"
                : "bg-white border border-slate-200 text-slate-700 hover:border-teal-400"
            }`}
          >
            {c.label}
          </button>
        ))}
        <select
          data-testid="products-sort-select"
          value={sort}
          onChange={(e) => update({ sort: e.target.value })}
          className="ml-auto rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:border-teal-500"
        >
          {SORTS.map((s) => (
            <option key={s.v} value={s.v}>
              {s.l}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div data-testid="products-loading" className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-2xl border border-slate-200 bg-white">
              <div className="aspect-square bg-slate-100" />
              <div className="p-4 space-y-2">
                <div className="h-4 rounded bg-slate-100" />
                <div className="h-4 w-1/2 rounded bg-slate-100" />
              </div>
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div data-testid="products-empty" className="mt-16 text-center">
          <p className="font-heading text-xl font-semibold text-slate-800">{t("empty_t")}</p>
          <p className="mt-2 text-sm text-slate-500">{t("empty_sub")}</p>
        </div>
      ) : (
        <div data-testid="products-grid" className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      {!imageResults && (
        <div className="mt-10 flex items-center justify-center gap-3">
          <button
            data-testid="products-prev-page"
            disabled={page <= 1}
            onClick={() => update({ page: String(page - 1) })}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-teal-400 disabled:opacity-40 transition"
          >
            <ChevronLeft className="h-4 w-4" /> {t("prev")}
          </button>
          <span data-testid="products-page-indicator" className="text-xs font-semibold text-slate-600">
            {t("page")} {page}
          </span>
          <button
            data-testid="products-next-page"
            disabled={products.length < LIMIT}
            onClick={() => update({ page: String(page + 1) })}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:border-teal-400 disabled:opacity-40 transition"
          >
            {t("next")} <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
